<template>
  <div class="row">
    <div class="col-12">
      <div class="ads-page-header d-md-flex align-items-center justify-content-between mb-20">
        <div class="d-flex align-items-center">
          <span class="ads-page-icon">
            <i class="flaticon-placeholder"></i>
          </span>
          <div class="ms-12">
            <h4 class="mb-0 fw-bold">Gestion des zones</h4>
            <p class="mb-0 text-muted fs-13">
              {{ zones.length }} zone{{ zones.length > 1 ? "s" : "" }} au total
            </p>
          </div>
        </div>
      </div>

      <div class="card ads-card mb-25 border-0 letter-spacing">
        <div
          class="card-head d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25"
        >
          <div class="d-sm-flex align-items-center">
            <button
              class="ads-btn-primary position-relative transition border-0 fw-medium text-white d-inline-flex align-items-center me-10 mb-10 mb-lg-0"
              type="button"
              data-bs-toggle="modal"
              data-bs-target="#createNewZoneModal"
              v-if="auth.can('SETTINGS')"
            >
              <i class="flaticon-plus position-relative fs-12 me-8"></i>
              Nouvelle zone
            </button>
            <button
              class="ads-btn-outline position-relative transition fw-medium d-inline-flex align-items-center mb-10 mb-lg-0"
              type="button"
            >
              <i class="flaticon-file-1 position-relative fs-15 me-8"></i>
              Export
            </button>
          </div>
          <div class="ads-search position-relative">
            <i class="flaticon-search ads-search-icon"></i>
            <input
              type="text"
              class="form-control ps-40"
              v-model="searchTerm"
              placeholder="Rechercher une zone..."
            />
          </div>
        </div>
        <div class="card-body p-15 p-sm-20 p-md-25 pt-0">
          <EasyDataTable
            :headers="headers"
            :items="zones"
            :loading="loading"
            :search-value="searchTerm"
            :theme-color="themeColor"
            alternating
            table-class-name="ads-table"
            buttons-pagination
          >
            <template #loading>
              <div class="skeleton-table w-100">
                <div class="loading-spinner-wrap text-center py-15">
                  <div class="spinner-border text-success" role="status">
                    <span class="visually-hidden">Chargement...</span>
                  </div>
                </div>
                <div
                  class="skeleton-row d-flex align-items-center"
                  v-for="n in 6"
                  :key="n"
                >
                  <div
                    class="skeleton-cell"
                    v-for="c in headers.length"
                    :key="c"
                  >
                    <span class="skeleton-bar"></span>
                  </div>
                </div>
              </div>
            </template>

            <template #empty-message>
              <div class="empty-state text-center py-40">
                <i class="flaticon-placeholder fs-40 d-block mb-10 text-muted"></i>
                <p class="mb-0 text-muted">Aucune zone trouvée</p>
              </div>
            </template>

            <!-- Custom "cities" column (array) -->
            <template #item-cities="{ cities }">
              <div class="d-flex flex-wrap gap-6">
                <span
                  class="status-pill status-pill-info"
                  v-for="(city, i) in cities"
                  :key="i"
                >
                  {{ city?.name }}
                </span>
                <span v-if="!cities || !cities.length" class="text-muted fs-13">—</span>
              </div>
            </template>

            <!-- Custom "active" column (status badge) -->
            <template #item-active="{ id, active }">
              <div class="switch">
                <input
                  type="checkbox"
                  class="switch-input"
                  :id="'switch-' + id"
                  :checked="active"
                  @change="toggleZoneActive(id, $event.target.checked)"
                />
                <label class="switch-label" :for="'switch-' + id"></label>
              </div>
            </template>

            <template #item-stock="{ id, stock }">
              <div class="switch">
                <input
                  type="checkbox"
                  class="switch-input"
                  :id="'switch-stock-' + id"
                  :checked="stock"
                  @change="toggleZoneStock(id, $event.target.checked)"
                />
                <label class="switch-label" :for="'switch-stock-' + id"></label>
              </div>
            </template>

            <template #item-hub="{ id, hub }">
              <div class="switch">
                <input
                  type="checkbox"
                  class="switch-input"
                  :id="'switch-hub-' + id"
                  :checked="hub"
                  @change="toggleZoneHub(id, $event.target.checked)"
                />
                <label class="switch-label" :for="'switch-hub-' + id"></label>
              </div>
            </template>

            <!-- Custom "actions" column -->
            <template #item-actions="{ id }">
              <div class="d-flex align-items-center gap-8" v-if="auth.can('SETTINGS')">
                <router-link
                  :to="`/cities-zone-settings/${id}`"
                  class="ads-icon-btn ads-icon-btn-info"
                  title="Villes"
                >
                  <i class="flaticon-placeholder"></i>
                </router-link>
                <button
                  type="button"
                  class="ads-icon-btn ads-icon-btn-edit"
                  title="Modifier"
                  @click="editZone(id)"
                >
                  <i class="flaticon-pen"></i>
                </button>
                <button
                  type="button"
                  class="ads-icon-btn ads-icon-btn-delete"
                  title="Supprimer"
                  @click="deleteZone(id)"
                >
                  <i class="flaticon-delete"></i>
                </button>
              </div>
            </template>
          </EasyDataTable>
        </div>
      </div>
    </div>
  </div>

  <div
    class="modal fade"
    id="createNewZoneModal"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content ads-modal-content">
        <div class="modal-header ads-modal-header">
          <div class="d-flex align-items-center">
            <span class="ads-modal-icon">
              <i class="flaticon-placeholder"></i>
            </span>
            <h5 class="modal-title ms-12 mb-0">Nouvelle zone</h5>
          </div>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitZone">
            <div class="row g-10">
              <div class="col-md-6">
                <div class="form-group mb-20">
                  <label class="d-block text-black fw-medium mb-10">
                    Réference de la zone
                  </label>
                  <input
                    type="text"
                    v-model="newZone.reference"
                    class="form-control"
                  />
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group mb-20">
                  <label class="d-block text-black fw-medium mb-10">
                    Nom de la zone
                  </label>
                  <input
                    type="text"
                    v-model="newZone.name"
                    class="form-control"
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer ads-modal-footer">
          <button
            type="button"
            class="ads-btn-outline transition fw-medium"
            data-bs-dismiss="modal"
          >
            Fermer
          </button>
          <button type="button" @click="submitZone" class="ads-btn-primary border-0 transition fw-medium">
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  </div>

  <div class="modal fade" id="editZoneModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content ads-modal-content">
        <div class="modal-header ads-modal-header">
          <div class="d-flex align-items-center">
            <span class="ads-modal-icon">
              <i class="flaticon-pen"></i>
            </span>
            <h5 class="modal-title ms-12 mb-0">Modifier zone</h5>
          </div>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitEditZone">
            <div class="row g-10">
              <div class="col-md-6">
                <div class="form-group mb-20">
                  <label class="d-block text-black fw-medium mb-10">
                    Réference de la zone
                  </label>
                  <input
                    type="text"
                    v-model="formZone.reference"
                    class="form-control"
                  />
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group mb-20">
                  <label class="d-block text-black fw-medium mb-10">
                    Nom de la zone
                  </label>
                  <input
                    type="text"
                    v-model="formZone.name"
                    class="form-control"
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer ads-modal-footer">
          <button
            type="button"
            class="ads-btn-outline transition fw-medium"
            data-bs-dismiss="modal"
          >
            Fermer
          </button>
          <button type="button" @click="submitEditZone" class="ads-btn-primary border-0 transition fw-medium">
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from "@/stores/auth";
import api from "@/utils/axios";
import { Modal } from "bootstrap";
import { onMounted, reactive, ref } from "vue";
import { useAppToast } from "@/composables/useAppToast";

export default {
  name: "ZonesContent",
  setup: () => {
    const auth = useAuthStore();
    const { showToast } = useAppToast();
    const themeColor = "#06b48a";
    const searchTerm = ref("");

    const newZone = reactive({
      reference: "",
      name: "",
    });

    const formZone = reactive({
      reference: "",
      name: "",
      id: "",
    });
    const headers = [
      { text: "Code", value: "ref" },
      { text: "Nom", value: "name" },
      { text: "Villes", value: "cities" },
      { text: "Stock", value: "stock" },
      { text: "Hub", value: "hub" },
      { text: "Statut", value: "active" },
      { text: "Actions", value: "actions" },
    ];

    const zones = ref([]);
    const loading = ref(true);

    onMounted(async () => {
      fetchZones();
    });

    async function submitZone() {
      if (!newZone.reference || !newZone.name) {
        showToast("Please fill in all fields!", "error");
        return;
      }

      try {
        // Example: send to API
        const response = await api.post("/api/auth/zone_add", {
          ref: newZone.reference,
          name: newZone.name,
        });

        if (response.data.message == "success") {
          const modalEl = document.getElementById("createNewZoneModal");
          const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
          modal.hide();
          fetchZones();
          this.newZone = {
            reference: "",
            name: "",
          };
          showToast("Zone added successfully!", "success");
          //   closeModal();
          // get zones list refresh
        } else {
          showToast("Error: " + response.data.message, "error");
        }
      } catch (error) {
        console.error(error);
        showToast("An error occurred while adding the zone.", "error");
      }
    }

    async function fetchZones() {
      loading.value = true;
      try {
        const response = await api.get("/api/auth/zone_list");
        if (response && response.data) {
          // handle the fetched zones data
          // console.log("Fetched zones:", response.data);
          zones.value = response.data;
        }
      } catch (error) {
        console.error("Error fetching zones:", error);
      } finally {
        loading.value = false;
      }
    }
    async function toggleZoneActive(id, value) {
      const zone = zones.value.find((z) => z.id === id);
      if (zone) {
        zone.active = value;
        // Optionally, send update to API here
        const res = await api.post(`/api/auth/active_zone/${id}`, {
          active: value,
        });
        console.log("Zone active status updated:", res.data);
      }
    }

    async function toggleZoneStock(id, value) {
      const zone = zones.value.find((z) => z.id === id);
      if (zone) {
        zone.stock = value;
        const res = await api.post(`/api/auth/stock_zone/${id}`, {
          active: value,
        });
        console.log("Zone active status updated:", res.data);
      }
    }
    async function toggleZoneHub(id, value) {
      const zone = zones.value.find((z) => z.id === id);
      if (zone) {
        zone.hub = value;
        const res = await api.post(`/api/auth/hub_zone/${id}`, {
          active: value,
        });
        console.log("Zone active status updated:", res.data);
      }
    }
    function editZone(id) {
      const zone = zones.value.find((z) => z.id === id);
      if (zone) {
        formZone.reference = zone.ref;
        formZone.name = zone.name;
        formZone.id = zone.id;
      }

      const modalEl = document.getElementById("editZoneModal");
      const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
      modal.show();
    }
    async function deleteZone(id) {
      if (confirm("Are you sure you want to delete this city?")) {
        try {
          const response = await api.post(`/api/auth/removeZone/${id}`);
          if (response.data.message === "success") {
            // Remove the city from the local list
            fetchZones();
            showToast("Zone deleted successfully!", "success");
          } else {
            showToast("Error deleting zone: " + response.data.message, "error");
          }
        } catch (error) {
          console.error(error);
          showToast("An error occurred while deleting the zone.", "error");
        }
      }
    }
    async function submitEditZone() {
      if (!formZone.reference || !formZone.name) {
        showToast("Please fill in all fields!", "error");
        return;
      }

      try {
        // Example: send to API
        const response = await api.post(`/api/auth/zone_edit/${formZone.id}`, {
          id: formZone.id,
          ref: formZone.reference,
          name: formZone.name,
        });

        if (response.data.message == "success") {
          const modalEl = document.getElementById("editZoneModal");
          const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
          modal.hide();
          fetchZones();
          formZone.reference = "";
          formZone.name = "";
          formZone.id = "";
          showToast("Zone updated successfully!", "success");
          //   closeModal();
          // get zones list refresh
        } else {
          showToast("Error: " + response.data.message, "error");
        }
      } catch (error) {
        console.error(error);
        showToast("An error occurred while updating the zone.", "error");
      }
    }
    return {
      newZone,
      submitZone,
      headers,
      zones,
      loading,
      themeColor,
      searchTerm,
      toggleZoneActive,
      toggleZoneHub,
      toggleZoneStock,
      deleteZone,
      editZone,
      auth,
      formZone,
      submitEditZone
    };
  },
};
</script>

<style scoped>
/* ---------- Page header ---------- */
.ads-page-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(6, 180, 138, 0.1);
  color: #06b48a;
  font-size: 20px;
}

/* ---------- Card ---------- */
.ads-card {
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 18px rgba(43, 42, 63, 0.06);
}

.ads-card .card-head {
  border-bottom: 1px solid #f0f0f5;
}

/* ---------- Buttons ---------- */
.ads-btn-primary {
  background: #06b48a;
  padding: 11px 22px;
  border-radius: 8px;
  font-size: 14px;
}

.ads-btn-primary:hover {
  background: #059c78;
  color: #fff;
}

.ads-btn-outline {
  background: transparent;
  border: 1px solid #e2e2ea;
  color: #2b2a3f;
  padding: 10px 22px;
  border-radius: 8px;
  font-size: 14px;
}

.ads-btn-outline:hover {
  background: #f8f8fa;
  border-color: #d5d5e0;
}

/* ---------- Search ---------- */
.ads-search {
  min-width: 260px;
}

.ads-search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #a2a1bb;
  font-size: 14px;
}

.ads-search .form-control {
  border-radius: 8px;
  border: 1px solid #e2e2ea;
  height: 44px;
}

.ads-search .form-control:focus {
  border-color: #06b48a;
  box-shadow: 0 0 0 3px rgba(6, 180, 138, 0.12);
}

/* ---------- Table ---------- */
:deep(.ads-table) {
  --easy-table-border: none;
  --easy-table-row-border: 1px solid #f1f1f5;

  --easy-table-header-font-size: 12px;
  --easy-table-header-height: 50px;
  --easy-table-header-font-color: #8e8da1;
  --easy-table-header-background-color: #f8f8fb;
  --easy-table-header-item-padding: 12px 16px;

  --easy-table-body-row-font-size: 14px;
  --easy-table-body-row-height: 60px;
  --easy-table-body-row-font-color: #2b2a3f;
  --easy-table-body-row-background-color: #ffffff;
  --easy-table-body-item-padding: 12px 16px;

  --easy-table-body-row-hover-font-color: #2b2a3f;
  --easy-table-body-row-hover-background-color: #f8fbfa;

  --easy-table-body-even-row-font-color: #2b2a3f;
  --easy-table-body-even-row-background-color: #fbfbfd;

  --easy-table-footer-font-color: #8e8da1;
  --easy-table-footer-font-size: 13px;
  --easy-table-footer-background-color: #ffffff;

  --easy-table-message-font-color: #8e8da1;

  --easy-table-loading-mask-background-color: #ffffff;
}

:deep(.ads-table th) {
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

/* ---------- Status pills ---------- */
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
}

.status-pill-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.status-pill-success {
  background: rgba(6, 180, 138, 0.1);
  color: #06b48a;
}

.status-pill-danger {
  background: rgba(239, 41, 41, 0.1);
  color: #ef2929;
}

.status-pill-warning {
  background: rgba(243, 196, 76, 0.18);
  color: #c9932a;
}

.status-pill-primary {
  background: rgba(101, 96, 240, 0.1);
  color: #6560f0;
}

.status-pill-info {
  background: rgba(31, 177, 230, 0.1);
  color: #1fb1e6;
}

/* ---------- Action icon buttons ---------- */
.ads-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #eceef1;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #8e8da1;
  transition: all 0.2s ease;
}

.ads-icon-btn-info:hover {
  background: rgba(31, 177, 230, 0.1);
  border-color: transparent;
  color: #1fb1e6;
}

.ads-icon-btn-edit:hover {
  background: rgba(101, 96, 240, 0.1);
  border-color: transparent;
  color: #6560f0;
}

.ads-icon-btn-delete:hover {
  background: rgba(239, 41, 41, 0.1);
  border-color: transparent;
  color: #ef2929;
}

/* ---------- Modal ---------- */
.ads-modal-content {
  border: none;
  border-radius: 14px;
  overflow: hidden;
}

.ads-modal-header {
  background: #f8f8fb;
  border-bottom: 1px solid #f0f0f5;
  padding: 18px 25px;
}

.ads-modal-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(6, 180, 138, 0.1);
  color: #06b48a;
}

.ads-modal-footer {
  border-top: 1px solid #f0f0f5;
  padding: 16px 25px;
}

.ads-modal-footer .ads-btn-primary,
.ads-modal-footer .ads-btn-outline {
  padding: 9px 20px;
}

/* ---------- Loading skeleton ---------- */
.skeleton-table {
  padding: 10px 0;
}

.skeleton-row {
  padding: 12px 0;
  border-bottom: 1px solid #f1f1f1;
  gap: 20px;
}

.skeleton-cell {
  flex: 1;
}

.skeleton-bar {
  display: block;
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(90deg, #eef0f2 25%, #e2e5e9 37%, #eef0f2 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

.empty-state {
  width: 100%;
}
</style>
